import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MasterPermissions from '../../../../api/MasterPermissions';
import GenericButton from '../../../../../Components/GenericButton/GenericButton';
import './styles/eobpreferences.scss';
import TextInput from '../../../../../Components/GenericInputText/TextInput';
import AutoSuggestionSelectComponent from '../../../common/auto-suggestion-select/Select';
import Skeleton from './Skeleton';
import { axiosInstance } from 'src/api/axios';
import APIEndpoints from 'src/Member/api/APIEndpoints';
import { HttpSuccessCodes } from 'src/Member/errorhandling/HttpErrorCodes';
import ToastMessage from 'src/Admin/components/common/ToastMessage';
import { toast } from 'react-toastify';

export const EOBPreferences = ({
  dataFetching,
  selectedMemberType,
  eobPreferences,
  setEobPreferences,
  selectedMemberOID,
  coverageFor,
}) => {
  const { MailPaperEOB, ReceiveMail, EobPreferenceMail } = eobPreferences;
  const { t } = useTranslation();

  const [loader, setLoader] = useState(dataFetching);

  const receivedEobAsOptions = [
    { label: "I don't need EOB", value: '0' },
    { label: 'Paper Copy by Mail Only', value: '1' },
    { label: 'Digital Copy by Email only', value: '2' },
    { label: 'Both - Paper and Email', value: '3' },
  ];

  const [receivedEobAs, setReceivedEobAs] = useState(null);

  const onReceivedEobAsOptionsChange = (e) => {
    setReceivedEobAs(e);
    setSendEmailToFieldError(false);
    if (e?.value === '2' || e?.value === '3') {
      setSendEobFieldRequired(true);
    } else {
      setSendEobFieldRequired(false);
    }
  };

  const [sentEmailTo, setSentEmailTo] = useState(EobPreferenceMail);

  const [sendEobFieldRequired, setSendEobFieldRequired] = useState(false);
  const [sendEmailToFieldError, setSendEmailToFieldError] = useState(false);

  const onSentEmailToChange = (e) => {
    setSentEmailTo(e?.target?.value);
    const regEmail =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (regEmail.test(e?.target?.value)) {
      setSendEmailToFieldError(false);
    } else {
      setSendEmailToFieldError(true);
    }
  };

  const handleBlur = (e) => {
    setSentEmailTo(e?.target?.value);
    const regEmail =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (regEmail.test(e?.target?.value)) {
      setSendEmailToFieldError(false);
    } else {
      setSendEmailToFieldError(true);
    }
  };

  const updateEOBPreferences = async () => {
    let urlString = '';

    let eobInformation = { ...eobPreferences };
    if (receivedEobAs.value === '0') {
      eobInformation = {
        ...eobInformation,
        MailPaperEOB: 'N',
        ReceiveMail: 'N',
      };
      urlString = `mailpapereob/N/receiveemail/N`;
    }
    if (receivedEobAs.value === '1') {
      eobInformation = {
        ...eobInformation,
        MailPaperEOB: 'Y',
        ReceiveMail: 'N',
      };
      urlString = `mailpapereob/Y/receiveemail/N`;
    }
    if (receivedEobAs.value === '2') {
      eobInformation = {
        ...eobInformation,
        MailPaperEOB: 'N',
        ReceiveMail: 'Y',
        EobPreferenceMail: sentEmailTo,
      };
      urlString = `mailpapereob/N/receiveemail/Y/email/${sentEmailTo}`;
    }
    if (receivedEobAs.value === '3') {
      eobInformation = {
        ...eobInformation,
        MailPaperEOB: 'Y',
        ReceiveMail: 'Y',
        EobPreferenceMail: sentEmailTo,
      };
      urlString = `mailpapereob/Y/receiveemail/Y/email/${sentEmailTo}`;
    }
    setEobPreferences(eobInformation);

    return await axiosInstance.put(
      `${APIEndpoints.updateMemberEOBPreferences(
        selectedMemberType,
      )}/oid/${selectedMemberOID}/${urlString}`,
    );
  };

  const [hipaaMembers, setHipaaMembers] = useState([]);
  const [count, setCount] = useState(1);
  const [apiUrl, setApiUrl] = useState('');

  useEffect(() => {
    if (coverageFor !== undefined && coverageFor.length > 1) {
      setHipaaMembers([...coverageFor]);
    }
  }, [coverageFor]);

  const updatePreference = async (dependentOID) => {
    try {
      await axiosInstance.put(
        `${APIEndpoints.updateMemberEOBPreferences(
          'Dependent',
        )}/oid/${dependentOID}/${apiUrl}`,
      );
      setCount((count) => count + 1);
      if (count + 1 === hipaaMembers.length) {
        toast.success(
          <ToastMessage
            type="success"
            subTitle={'EOB preferences updated successfully'}
          />,
        );
      }
    } catch (error) {
      console.log(error);
      toast.error(
        <ToastMessage
          type="error"
          subTitle={'Error in updating EOB Preferences'}
        />,
      );
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (apiUrl != '' && hipaaMembers.length > 1) {
      const eachMember = hipaaMembers[count];
      if (hipaaMembers.length > count) {
        updatePreference(eachMember.value);
      }
    }
  }, [apiUrl, count, hipaaMembers]);

  const handleMemberAndDependentSave = async () => {
    setLoader(true);
    try {
      const response = await updateEOBPreferences();
      let urlString = '';
      if (hipaaMembers?.length > 1) {
        if (receivedEobAs.value === '0') {
          urlString = `mailpapereob/N/receiveemail/N`;
        }
        if (receivedEobAs.value === '1') {
          urlString = `mailpapereob/Y/receiveemail/N`;
        }
        if (receivedEobAs.value === '2') {
          urlString = `mailpapereob/N/receiveemail/Y`;
        }
        if (receivedEobAs.value === '3') {
          urlString = `mailpapereob/Y/receiveemail/Y`;
        }
        setApiUrl(urlString);
        setCount(1);
      } else {
        toast.success(
          <ToastMessage
            type="success"
            subTitle={'EOB preferences updated successfully'}
          />,
        );
      }
    } catch (error) {
      console.log(error);

      toast.error(
        <ToastMessage
          type="error"
          subTitle={'Error in updating EOB Preferences'}
        />,
      );
    } finally {
      setLoader(false);
    }
  };

  const handleSave = async () => {
    setLoader(true);
    console.log(coverageFor);
    try {
      const response = await updateEOBPreferences();
      if (HttpSuccessCodes.HTTP_SUCCESS_CODE_200 === response.status) {
        //dispatch(setAppConfig(updatedAppConfigs));
      }
      toast.success(
        <ToastMessage
          type="success"
          subTitle={'EOB preferences updated successfully'}
        />,
      );
    } catch (error) {
      console.log(error);

      toast.error(
        <ToastMessage
          type="error"
          subTitle={'Error in updating EOB Preferences'}
        />,
      );
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    setLoader(dataFetching);
    if (!dataFetching) {
      setSentEmailTo(EobPreferenceMail != null ? EobPreferenceMail : '');
      if (MailPaperEOB === 'N' && ReceiveMail === 'N') {
        setReceivedEobAs(receivedEobAsOptions[0]);
      } else if (MailPaperEOB === 'Y' && ReceiveMail === 'N') {
        setReceivedEobAs(receivedEobAsOptions[1]);
      } else if (MailPaperEOB === 'N' && ReceiveMail === 'Y') {
        setReceivedEobAs(receivedEobAsOptions[2]);
        setSendEobFieldRequired(true);
      } else if (MailPaperEOB === 'Y' && ReceiveMail === 'Y') {
        setReceivedEobAs(receivedEobAsOptions[3]);
        setSendEobFieldRequired(true);
      }
    }
  }, [dataFetching]);

  return (
    <>
      <h2 className="row-title hide-row-title" tabIndex={0}>
        {t('member.pages.my_profile.eob_preferences.title')}
      </h2>
      {loader ? (
        <Skeleton />
      ) : (
        <div className="eob-preferences-details-container">
          <div className="d-flex align-items-center eob-content">
            {t('member.pages.my_profile.eob_preferences.content')}
          </div>
          <div className="input-container">
            <div className="recevied-eob">
              <span className="input-label">Receive EOB as</span>
              <AutoSuggestionSelectComponent
                options={receivedEobAsOptions}
                components={{ IndicatorSeparator: () => null }}
                inputClassName=""
                value={receivedEobAs}
                onChange={(e) => {
                  onReceivedEobAsOptionsChange(e);
                }}
                isSearchable={false}
                id="receviedEob"
                labelText="Receive EOB as"
              />
            </div>
            {sendEobFieldRequired && (
              <div className="send-eob-email">
                <TextInput
                  parentClassName="send-eob-email"
                  label="Send Email to"
                  isRequired={sendEobFieldRequired}
                  feedBackMessage={
                    sendEmailToFieldError
                      ? 'Please enter valid email address'
                      : ''
                  }
                  inputProps={{
                    type: 'email',
                    spellCheck: 'off',
                    name: 'send-eob-email',
                    autoCorrect: 'off',
                    autoComplete: 'off',
                    onChange: onSentEmailToChange,
                    value: sentEmailTo,
                    placeholder: 'Enter your preferred email',
                    isInvalid: sendEmailToFieldError,
                    id: 'send-eob-email',
                    onBlur: handleBlur,
                  }}
                  ariaInvalid={sendEmailToFieldError ? true : null}
                />
              </div>
            )}
          </div>

          <div className="controller-container">
            <GenericButton
              //type="submit"
              onClick={handleSave}
              buttonDisabled={
                sendEobFieldRequired
                  ? sentEmailTo !== '' &&
                    sentEmailTo !== null &&
                    !sendEmailToFieldError
                    ? false
                    : true
                  : false
              }
              disable={
                sendEobFieldRequired
                  ? sentEmailTo !== '' &&
                    sentEmailTo !== null &&
                    !sendEmailToFieldError
                    ? false
                    : true
                  : false
              }
              variant="primary"
            >
              Update member preference
            </GenericButton>

            {selectedMemberType === 'Employee' && (
              <GenericButton
                //type="submit"
                onClick={handleMemberAndDependentSave}
                buttonDisabled={
                  sendEobFieldRequired
                    ? sentEmailTo !== '' &&
                      sentEmailTo !== null &&
                      !sendEmailToFieldError
                      ? false
                      : true
                    : false
                }
                disable={
                  sendEobFieldRequired
                    ? sentEmailTo !== '' &&
                      sentEmailTo !== null &&
                      !sendEmailToFieldError
                      ? false
                      : true
                    : false
                }
                variant="secondary"
              >
                Update for member and dependents
              </GenericButton>
            )}
          </div>
        </div>
      )}
    </>
  );
};
